/** TipTap nodes of the post editor. Output must match the HTML contract in articleSchema.js. */
import React from 'react';
import { Mark, Node, Extension, mergeAttributes, textblockTypeInputRule } from '@tiptap/core';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import CodeBlock from '@tiptap/extension-code-block';
import { Button, Space, Tooltip } from 'antd';
import { CloseOutlined, DeleteOutlined, FileImageOutlined, LinkOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../../shared/i18n';
import { CODE_LANGUAGES, embedProvider } from './articleSchema';

const imgOf = (el) => {
    const i = el.tagName === 'IMG' ? el : el.querySelector('img');
    return { src: i?.getAttribute('src') || '', alt: i?.getAttribute('alt') || '', width: i?.getAttribute('width') || null, height: i?.getAttribute('height') || null };
};
const capOf = (el) => el.querySelector?.('figcaption')?.textContent || '';
const cap = (caption) => (caption ? [['figcaption', {}, caption]] : []);
const Caption = ({ value, onChange, placeholder }) => (
    <input className="yc-be-cap" value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
);

// ---------------------------------------------------------------------------- image
const FigureView = ({ node, updateAttributes, deleteNode, selected }) => {
    const { t } = useTranslation();
    const a = node.attrs;
    return (
        <NodeViewWrapper as="figure" className={`yc-be-fig${selected ? ' is-sel' : ''}`} data-drag-handle="">
            <div className="yc-be-fig-box">
                <img src={a.src} alt={a.alt} width={a.width || undefined} height={a.height || undefined} draggable={false} />
                <Tooltip title={t('Delete')}><Button className="yc-be-x" size="small" icon={<DeleteOutlined />} onClick={deleteNode} /></Tooltip>
            </div>
            <Caption value={a.caption} onChange={(v) => updateAttributes({ caption: v })} placeholder={t('Add a caption')} />
            <input className="yc-be-alt" value={a.alt || ''} placeholder={t('Alt text')} onChange={(e) => updateAttributes({ alt: e.target.value })} />
        </NodeViewWrapper>
    );
};
export const FigureImage = Node.create({
    name: 'figureImage',
    group: 'block',
    atom: true,
    draggable: true,
    selectable: true,
    addAttributes() { return { src: { default: '' }, alt: { default: '' }, width: { default: null }, height: { default: null }, caption: { default: '' } }; },
    parseHTML() {
        return [
            { tag: 'figure', getAttrs: (el) => (el.hasAttribute('data-embed') || el.classList.contains('gallery') || el.querySelectorAll('img').length !== 1 ? false : { ...imgOf(el), caption: capOf(el) }) },
            { tag: 'img[src]', getAttrs: (el) => imgOf(el) },
        ];
    },
    renderHTML({ node }) {
        const { src, alt, width, height, caption } = node.attrs;
        const img = { src, alt: alt || '' };
        if (width) img.width = width;
        if (height) img.height = height;
        return ['figure', {}, ['img', img], ...cap(caption)];
    },
    addNodeView() { return ReactNodeViewRenderer(FigureView); },
});

// ---------------------------------------------------------------------------- gallery
const GalleryView = ({ node, updateAttributes, deleteNode, extension }) => {
    const { t } = useTranslation();
    const imgs = node.attrs.images || [];
    const add = (rows) => updateAttributes({ images: [...imgs, ...rows.map((m) => ({ src: m.url, alt: m.alt || '' }))] });
    const ctx = () => extension.options.ctx.current;
    return (
        <NodeViewWrapper as="figure" className="yc-be-fig">
            <div className="yc-be-gal">
                {imgs.map((m, i) => (
                    <div key={`${m.src}-${i}`} className="yc-be-fig-box">
                        <img src={m.src} alt={m.alt} draggable={false} />
                        <Button className="yc-be-x" size="small" icon={<CloseOutlined />} aria-label={t('Remove')} onClick={() => updateAttributes({ images: imgs.filter((_, k) => k !== i) })} />
                    </div>
                ))}
            </div>
            <Space size={6} style={{ marginTop: 6 }}>
                <Button size="small" icon={<FileImageOutlined />} onClick={() => ctx().pick((m) => add([m]))}>{t('Media library')}</Button>
                <Button size="small" icon={<UploadOutlined />} onClick={() => ctx().chooseFiles(add)}>{t('Upload')}</Button>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={deleteNode}>{t('Delete')}</Button>
            </Space>
            <Caption value={node.attrs.caption} onChange={(v) => updateAttributes({ caption: v })} placeholder={t('Add a caption')} />
        </NodeViewWrapper>
    );
};
export const Gallery = Node.create({
    name: 'gallery',
    group: 'block',
    atom: true,
    draggable: true,
    addOptions() { return { ctx: { current: {} } }; },
    addAttributes() { return { images: { default: [] }, caption: { default: '' } }; },
    parseHTML() {
        return [{
            tag: 'figure',
            priority: 60,
            getAttrs: (el) => {
                if (el.hasAttribute('data-embed')) return false;
                const list = [...el.querySelectorAll('img')];
                if (!el.classList.contains('gallery') && list.length < 2) return false;
                return { images: list.map((i) => ({ src: i.getAttribute('src'), alt: i.getAttribute('alt') || '' })), caption: capOf(el) };
            },
        }];
    },
    renderHTML({ node }) {
        return ['figure', { class: 'gallery' }, ...(node.attrs.images || []).map((m) => ['img', { src: m.src, alt: m.alt || '' }]), ...cap(node.attrs.caption)];
    },
    addNodeView() { return ReactNodeViewRenderer(GalleryView); },
});

// ---------------------------------------------------------------------------- embed
const EmbedView = ({ node, updateAttributes, deleteNode }) => {
    const { t } = useTranslation();
    const name = embedProvider(node.attrs.url);
    return (
        <NodeViewWrapper as="figure" className="yc-be-fig">
            <div className="yc-be-embed">
                <LinkOutlined />
                <b>{name || t('Link')}</b>
                <a href={node.attrs.url} target="_blank" rel="noopener noreferrer">{node.attrs.url}</a>
                <Button size="small" type="text" icon={<DeleteOutlined />} aria-label={t('Delete')} onClick={deleteNode} />
            </div>
            <Caption value={node.attrs.caption} onChange={(v) => updateAttributes({ caption: v })} placeholder={t('Add a caption')} />
        </NodeViewWrapper>
    );
};
export const Embed = Node.create({
    name: 'embed',
    group: 'block',
    atom: true,
    draggable: true,
    addAttributes() { return { url: { default: '' }, caption: { default: '' } }; },
    parseHTML() { return [{ tag: 'figure[data-embed]', priority: 70, getAttrs: (el) => ({ url: el.getAttribute('data-embed'), caption: capOf(el) }) }]; },
    renderHTML({ node }) { return ['figure', { 'data-embed': node.attrs.url }, ...cap(node.attrs.caption)]; },
    addNodeView() { return ReactNodeViewRenderer(EmbedView); },
});

// ---------------------------------------------------------------------------- callout
export const Callout = Node.create({
    name: 'callout',
    group: 'block',
    content: 'block+',
    defining: true,
    parseHTML() { return [{ tag: 'aside.callout' }, { tag: 'aside' }]; },
    renderHTML() { return ['aside', { class: 'callout' }, 0]; },
});

// ---------------------------------------------------------------------------- footnote (numbered on save, see toStoredHtml)
export const Footnote = Node.create({
    name: 'footnote',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    addAttributes() { return { text: { default: '', parseHTML: (el) => el.getAttribute('data-fn') || '' } }; },
    parseHTML() { return [{ tag: 'sup[data-fn]', priority: 80 }]; },
    renderHTML({ node }) { return ['sup', { 'data-fn': node.attrs.text, class: 'yc-be-fn', title: node.attrs.text }]; },
});

// ---------------------------------------------------------------------------- code block with language
const CodeView = ({ node, updateAttributes }) => {
    const { t } = useTranslation();
    return (
        <NodeViewWrapper className="yc-be-code">
            <select contentEditable={false} aria-label={t('Language')} value={node.attrs.language || 'plaintext'} onChange={(e) => updateAttributes({ language: e.target.value === 'plaintext' ? null : e.target.value })}>
                {CODE_LANGUAGES.concat(node.attrs.language && !CODE_LANGUAGES.includes(node.attrs.language) ? [node.attrs.language] : []).map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <pre><NodeViewContent as="code" /></pre>
        </NodeViewWrapper>
    );
};
export const CodeBlockLang = CodeBlock.extend({ addNodeView() { return ReactNodeViewRenderer(CodeView); } });

// ---------------------------------------------------------------------------- small marks the site keeps
const simpleMark = (name, tag) => Mark.create({
    name,
    parseHTML() { return [{ tag }]; },
    renderHTML({ HTMLAttributes }) { return [tag, mergeAttributes(HTMLAttributes), 0]; },
});
export const MarkHighlight = simpleMark('highlightMark', 'mark');
export const MarkSub = simpleMark('subMark', 'sub');
export const MarkSup = simpleMark('supMark', 'sup');

/** "# " makes the first heading level the body has (the post title is the h1). */
export const HeadingShortcut = Extension.create({
    name: 'headingShortcut',
    addInputRules() {
        return [textblockTypeInputRule({ find: /^#\s$/, type: this.editor.schema.nodes.heading, getAttributes: () => ({ level: 2 }) })];
    },
});

